import { collection, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useAuth, useDb, useEnsureAuth } from '../firebase/FirebaseContext';
import { giftActivityCollectionPath } from '../schemas/paths';
import type { Recipient } from '../schemas';
import type { GiftActivityDetail } from './useGiftActivities';
import { decodeHtmlEntities } from '../lib/decodeHtmlEntities';
import { addedLinkTitle } from '../lib/addedLink';

// One saved/purchased activity with the recipient it belongs to. The same
// productId can appear once per recipient (saved for Mom AND for Sis), so
// (recipientId, id) — not id alone — is the unique key.
export interface AllSavedActivity extends GiftActivityDetail {
  recipientId: string;
  state: 'SAVED' | 'PURCHASED';
  createdAtMs: number;
}

export interface UseAllSavedActivitiesResult {
  /** SAVED across all recipients (newest first), then PURCHASED items not
   *  also saved for that same recipient (newest first) — the same ordering
   *  the per-recipient /liked page uses, extended across people. */
  activities: AllSavedActivity[];
  /** productIds with state='SAVED', keyed by recipientId. */
  likedByRecipient: Record<string, ReadonlySet<string>>;
  /** productIds with state='PURCHASED', keyed by recipientId. */
  purchasedByRecipient: Record<string, ReadonlySet<string>>;
  /** True once every recipient's giftActivity listener has delivered its
   *  first snapshot (vacuously true with zero recipients). */
  hydrated: boolean;
}

interface PerRecipientState {
  entries: Record<string, AllSavedActivity[]>;
  hydratedIds: ReadonlySet<string>;
}

const EMPTY_STATE: PerRecipientState = { entries: {}, hydratedIds: new Set() };

// Backs the all-saves board (/liked): per-recipient onSnapshot fan-out over
// `theaWebUser/{uid}/recipient/{rid}/giftActivity`, merged client-side. Same
// fan-out pattern and rationale as useSavedProductMap / useTotalLikedCount —
// a collectionGroup query can't be scoped to the caller (no uid field on the
// docs). Details come straight off the frozen productSnapshot, so cards
// render with no extra product fetch.
export function useAllSavedActivities(
  recipients: Recipient[],
): UseAllSavedActivitiesResult {
  const auth = useAuth();
  const db = useDb();
  const ensureAuth = useEnsureAuth();
  const [uid, setUid] = useState<string | null>(auth.currentUser?.uid ?? null);
  const [state, setState] = useState<PerRecipientState>(EMPTY_STATE);

  // Key the subscribe effect on the recipient ID SET, not array identity —
  // useRecipients re-emits a fresh array on content-equal re-deliveries
  // (tab refocus, metadata pings), and re-running the effect on those would
  // tear down every listener and flash the page skeleton for nothing. Same
  // churn class useGiftActivities suppresses with its setsEqual reuse.
  const recipientIdsKey = useMemo(
    () =>
      recipients
        .map((r) => r.recipientId)
        .sort()
        .join('|'),
    [recipients],
  );
  const recipientsRef = useRef(recipients);
  recipientsRef.current = recipients;

  useEffect(() => {
    return onAuthStateChanged(auth, (user) => {
      const nextUid = user?.uid ?? null;
      setUid((prev) => {
        if (prev === nextUid) return prev;
        setState(EMPTY_STATE);
        return nextUid;
      });
    });
  }, [auth]);

  useEffect(() => {
    let cancelled = false;
    const unsubscribers: Array<() => void> = [];
    // Snapshot the list this run subscribes for — the ref always holds the
    // latest array, but this effect only re-runs when the ID SET changes,
    // so the run's own list must stay stable.
    const subscribed = recipientsRef.current;

    // Re-baseline whenever the subscription set changes (uid swap already
    // reset above; this covers recipients arriving/changing) so entries from
    // a recipient no longer in the list don't linger.
    setState(EMPTY_STATE);

    if (subscribed.length === 0) {
      return () => undefined;
    }

    // Defensive: same guard as useRecipients/useSavedProductMap for test
    // contexts that mock firebaseConfig without ensureAuth.
    if (typeof ensureAuth !== 'function') {
      return () => undefined;
    }

    // Bind the identity this effect run was SCHEDULED for, not whatever
    // `ensureAuth()` happens to resolve. During an anon→permanent transition
    // `ensureAuth()` can hand back the OLD uid a beat late (a stale in-flight
    // anon sign-in, or a separate hook instance); `uid` from the authoritative
    // `onAuthStateChanged` is this run's real identity (a newer one would have
    // cancelled it). `resolvedUid` is used ONLY to bootstrap the null case
    // (cold load / fresh anon mint). Captured once, it targets the whole
    // per-recipient fan-out below, so a settled account is never stranded.
    const expectedUid = uid;

    ensureAuth()
      .then((resolvedUid) => {
        if (cancelled) return;
        // Known identity → bind it directly; `resolvedUid` bootstraps null.
        const targetUid = expectedUid ?? resolvedUid;
        subscribed.forEach((r) => {
          const ref = collection(
            db,
            ...giftActivityCollectionPath(targetUid, r.recipientId),
          );
          const unsub = onSnapshot(
            ref,
            (snap) => {
              if (cancelled) return;
              const next: AllSavedActivity[] = [];
              snap.forEach((d) => {
                const data = d.data() as {
                  state?: string;
                  kind?: string;
                  affiliateUrl?: string;
                  purchasedAt?: { toMillis?: () => number; seconds?: number };
                  createdAt?: { toMillis?: () => number; seconds?: number };
                  productSnapshot?: {
                    title?: string;
                    brand?: string;
                    price?: number;
                    imageUrl?: string;
                    url?: string;
                  };
                  addedLink?: { name?: string; storeHost?: string };
                };
                if (data.state !== 'SAVED' && data.state !== 'PURCHASED') return;
                const isLink = data.kind === 'LINK';
                const snapshot = data.productSnapshot;
                // THE-1084 PR4: a kind:'LINK' doc has no productSnapshot — its
                // title is synthesized from `addedLink` instead, so it's never
                // empty and never falls out of this all-saves list (previously
                // the same gap useGiftActivities had before PR4).
                //
                // Storage contract (theaWebRecordAddedLink): `addedLink` only
                // ever carries `{name?, storeHost}`; the URL is top-level
                // `affiliateUrl`. Only `storeHost` gates a LINK doc — requiring
                // `addedLink.url` (which never exists) silently dropped every
                // LINK save.
                const addedLink = isLink && data.addedLink?.storeHost && data.affiliateUrl
                  ? {
                      url: data.affiliateUrl,
                      storeHost: data.addedLink.storeHost,
                      // Shown exactly as typed (spec §6) — plain user text,
                      // not merchant-encoded catalog text — never decoded.
                      ...(data.addedLink.name ? { name: data.addedLink.name } : {}),
                    }
                  : undefined;
                const title = addedLink
                  ? addedLinkTitle(addedLink)
                  : decodeHtmlEntities(snapshot?.title ?? '');
                // A legacy activity without a snapshot can't render a card.
                if (!title) return;
                const entry: AllSavedActivity = {
                  id: d.id,
                  title,
                  recipientId: r.recipientId,
                  state: data.state,
                  purchasedAtMs: typeof data.purchasedAt?.toMillis === 'function'
                    ? data.purchasedAt.toMillis() : (data.purchasedAt?.seconds ?? 0) * 1000,
                  createdAtMs:
                    typeof data.createdAt?.toMillis === 'function'
                      ? data.createdAt.toMillis()
                      : typeof data.createdAt?.seconds === 'number'
                        ? data.createdAt.seconds * 1000
                        : 0,
                };
                if (isLink) entry.kind = 'LINK';
                if (addedLink) {
                  entry.addedLink = addedLink;
                  entry.productUrl = addedLink.url;
                }
                if (snapshot?.brand) entry.brand = decodeHtmlEntities(snapshot.brand);
                if (typeof snapshot?.price === 'number') entry.price = snapshot.price;
                if (snapshot?.imageUrl) entry.imageUrl = snapshot.imageUrl;
                if (snapshot?.url) entry.productUrl = snapshot.url;
                next.push(entry);
              });
              setState((prev) => {
                const hydratedIds = prev.hydratedIds.has(r.recipientId)
                  ? prev.hydratedIds
                  : new Set(prev.hydratedIds).add(r.recipientId);
                return {
                  entries: { ...prev.entries, [r.recipientId]: next },
                  hydratedIds,
                };
              });
            },
            () => {
              if (cancelled) return;
              // A failed listener still counts as hydrated so one bad
              // recipient doesn't shimmer the whole page forever.
              setState((prev) => ({
                entries: prev.entries,
                hydratedIds: prev.hydratedIds.has(r.recipientId)
                  ? prev.hydratedIds
                  : new Set(prev.hydratedIds).add(r.recipientId),
              }));
            },
          );
          unsubscribers.push(unsub);
        });
      })
      .catch(() => {
        // Auth never resolved — mark everything hydrated so the page's
        // skeleton stays BOUNDED (empty state beats an infinite shimmer;
        // same hydrated-on-error stance as useGiftActivities).
        if (cancelled) return;
        setState((prev) => ({
          entries: prev.entries,
          hydratedIds: new Set(subscribed.map((r) => r.recipientId)),
        }));
      });

    return () => {
      cancelled = true;
      unsubscribers.forEach((u) => u());
    };
    // recipientIdsKey stands in for `recipients` (content key, see above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, recipientIdsKey, db, ensureAuth]);

  const likedByRecipient = useMemo(() => {
    const out: Record<string, ReadonlySet<string>> = {};
    for (const [rid, entries] of Object.entries(state.entries)) {
      const ids = new Set<string>();
      for (const e of entries) if (e.state === 'SAVED') ids.add(e.id);
      out[rid] = ids;
    }
    return out;
  }, [state.entries]);

  const purchasedByRecipient = useMemo(() => {
    const out: Record<string, ReadonlySet<string>> = {};
    for (const [rid, entries] of Object.entries(state.entries)) {
      const ids = new Set<string>();
      for (const e of entries) if (e.state === 'PURCHASED') ids.add(e.id);
      out[rid] = ids;
    }
    return out;
  }, [state.entries]);

  const activities = useMemo(() => {
    const saved: AllSavedActivity[] = [];
    const purchased: AllSavedActivity[] = [];
    for (const [rid, entries] of Object.entries(state.entries)) {
      const likedIds = likedByRecipient[rid];
      for (const e of entries) {
        if (e.state === 'SAVED') saved.push(e);
        else if (!likedIds?.has(e.id)) purchased.push(e);
      }
    }
    // Newest first within each band; missing/0 createdAt sorts to the end so
    // a legacy activity without the field doesn't displace fresh saves.
    saved.sort((a, b) => b.createdAtMs - a.createdAtMs);
    purchased.sort((a, b) => b.createdAtMs - a.createdAtMs);
    return [...saved, ...purchased];
  }, [state.entries, likedByRecipient]);

  const hydrated = useMemo(
    () => recipients.every((r) => state.hydratedIds.has(r.recipientId)),
    [recipients, state.hydratedIds],
  );

  return { activities, likedByRecipient, purchasedByRecipient, hydrated };
}
